import { Link, router } from "@inertiajs/react"
import { Play, Skull } from "lucide-react"
import { useMemo } from "react"

import { commentatorNeon } from "@/components/commentator-neon"
import { NeonDivider } from "@/components/neon-divider"
import { PanelFrame } from "@/components/panel-frame"
import { RulePopover } from "@/components/rule-popover"
import { SegmentHeader } from "@/components/segment-header"
import { SmallPortrait } from "@/components/small-portrait"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useRevealOnScroll } from "@/hooks/use-reveal-on-scroll"
import { leagueTeamPlayerPath } from "@/routes"
import type {
  MatchInjury,
  MatchMvp,
  MatchPageProps,
  MatchRecap,
  MatchSppRow,
  MatchStats,
} from "@/types"

import { StatComparisonBars } from "./stat-comparison-bars"

const TIMING = {
  revealDuration: 0.6, // s — scroll-triggered desk entrance
  revealOffset: 24, // px — translateY start offset before reveal
}

// ── MVP Spotlight ────────────────────────────────────────────────────

function MvpSpotlight({
  mvps,
  match,
  commentatorKey,
  commentatorName,
  portraitUrl,
}: {
  mvps: MatchMvp[]
  match: MatchPageProps["match"]
  commentatorKey: string
  commentatorName: string
  portraitUrl: string | null
}) {
  const c = commentatorNeon(commentatorKey)

  return (
    <div>
      {mvps.map((mvp) => {
        const teamName =
          mvp.team_id === match.home_team.id
            ? match.home_team.name
            : match.away_team.name

        return (
          <div key={mvp.player_id} className="mb-3 flex items-start gap-3">
            {/* Commentator portrait */}
            <SmallPortrait
              commentatorKey={commentatorKey}
              name={commentatorName}
              portraitUrl={portraitUrl}
            />

            {/* Speech bubble */}
            <div
              className="min-w-0 flex-1"
              style={{
                background: "oklch(0.10 0.02 55 / 0.80)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                marginLeft: -8,
              }}
            >
              <div className="px-4 py-2.5">
                <span
                  className="mb-0.5 block text-[10px] font-black tracking-wider uppercase"
                  style={{
                    color: c.neon,
                    textShadow: `0 0 8px ${c.glow}, 0 0 16px ${c.soft}`,
                  }}
                >
                  {commentatorName}
                </span>
                <p className="text-xs leading-relaxed text-[var(--foreground)]">
                  <span className="font-semibold">{mvp.player_name}</span> takes
                  home the MVP award —{" "}
                  <span className="text-[var(--neon-gold)]">
                    +{mvp.spp_earned} SPP
                  </span>{" "}
                  for the {teamName} {mvp.position}.
                </p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Injury Ticker ────────────────────────────────────────────────────

function InjuryTicker({
  injuries,
  match,
}: {
  injuries: MatchInjury[]
  match: MatchPageProps["match"]
}) {
  const significantInjuries = injuries.filter((i) => i.status_change !== "none")
  if (significantInjuries.length === 0) return null

  return (
    <div>
      <SegmentHeader
        rules={false}
        as="h3"
        className="mb-2 text-[10px] tracking-[0.2em]"
        label="Casualties"
      />
      <div className="flex flex-wrap gap-2">
        {significantInjuries.map((injury, idx) => {
          const isDead = injury.status_change === "dead"
          const isMng = injury.status_change === "miss_next"
          const teamName =
            injury.team_id === match.home_team.id
              ? match.home_team.name
              : match.away_team.name

          return (
            <div
              key={`${injury.player_id}-${idx}`}
              className="flex items-center gap-1.5 px-2 py-1"
              style={{
                background: isDead
                  ? "oklch(0.18 0.04 28 / 0.3)"
                  : "oklch(0.18 0.04 55 / 0.3)",
              }}
            >
              {isDead && <Skull className="text-destructive size-3" />}
              <span
                className={`text-xs font-semibold ${isDead ? "text-destructive" : "text-[var(--foreground)]"}`}
              >
                {injury.player_name}
              </span>
              <span
                className="text-[10px]"
                style={{ color: "var(--muted-foreground)" }}
              >
                {teamName}
              </span>
              <RulePopover
                category="injury"
                ruleKey={
                  isDead ? "dead" : isMng ? "miss_next" : injury.injury_type
                }
              >
                <Badge
                  variant={isDead ? "destructive" : "outline"}
                  className="cursor-pointer px-1 py-0 text-[10px]"
                >
                  {isDead ? "Dead" : isMng ? "MNG" : injury.injury_type}
                </Badge>
              </RulePopover>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Top Performers ───────────────────────────────────────────────────

function TopPerformers({
  sppEarned,
  match,
  leagueId,
}: {
  sppEarned: MatchSppRow[]
  match: MatchPageProps["match"]
  leagueId: string
}) {
  const top = useMemo(
    () => [...sppEarned].sort((a, b) => b.total - a.total).slice(0, 4),
    [sppEarned],
  )

  if (top.length === 0) return null

  return (
    <div>
      <SegmentHeader
        rules={false}
        as="h3"
        className="mb-3 text-center text-[10px] tracking-[0.2em]"
        label="Top Performers"
      />
      <div className="grid gap-2 sm:grid-cols-2">
        {top.map((row) => {
          const teamHue =
            row.team_id === match.home_team.id
              ? (match.home_team.color_hue ?? 55)
              : (match.away_team.color_hue ?? 280)

          const teamColor = `oklch(0.55 0.18 ${teamHue})`

          return (
            <div
              key={row.player_id}
              className="relative flex items-center gap-3"
              style={{ background: "oklch(0.10 0.02 55 / 0.6)" }}
            >
              {/* Top-left accent ledge */}
              <span
                className="pointer-events-none absolute top-0 left-0 h-px w-8"
                aria-hidden="true"
                style={{
                  background: `linear-gradient(to right, ${teamColor}, transparent)`,
                }}
              />
              <span
                className="pointer-events-none absolute top-0 left-0 h-4 w-px"
                aria-hidden="true"
                style={{
                  background: `linear-gradient(to bottom, ${teamColor}, transparent)`,
                }}
              />
              <div className="min-w-0 flex-1 py-2 pl-3">
                <Link
                  href={leagueTeamPlayerPath(
                    leagueId,
                    row.team_id,
                    row.player_id,
                  )}
                  className="block truncate text-sm font-semibold text-[var(--foreground)] transition-colors hover:text-[var(--accent)]"
                >
                  {row.player_name}
                </Link>
                <span
                  className="text-[10px]"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  {row.position}
                </span>
              </div>
              <span
                className="flex-shrink-0 pr-3 text-sm font-bold"
                style={{ color: "var(--neon-gold)" }}
              >
                +{row.total}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── PostMatchDesk ────────────────────────────────────────────────────

interface PostMatchDeskProps {
  stats: MatchStats
  match: MatchPageProps["match"]
  mvps: MatchMvp[]
  injuries: MatchInjury[]
  sppEarned: MatchSppRow[]
  recap: MatchRecap
  replayHref: string
  backHref: string
  leagueName: string
  leagueId: string
}

export function PostMatchDesk({
  stats,
  match,
  mvps,
  injuries,
  sppEarned,
  recap,
  replayHref,
  backHref,
  leagueName,
  leagueId,
}: PostMatchDeskProps) {
  const { ref, revealed } = useRevealOnScroll()
  const announcer = recap.commentators[0]

  return (
    <div
      ref={ref}
      className="mx-auto max-w-3xl px-4 pt-16 pb-8"
      style={{
        opacity: revealed ? 1 : 0,
        transform: revealed
          ? "translateY(0)"
          : `translateY(${TIMING.revealOffset}px)`,
        transition: `opacity ${TIMING.revealDuration}s ease-out, transform ${TIMING.revealDuration}s ease-out`,
      }}
    >
      {/* Panel with neon frame */}
      <div className="relative">
        <PanelFrame />

        <div
          className="relative overflow-visible pt-10 pb-8"
          style={{
            background: "oklch(0.14 0.008 240)",
            boxShadow:
              "0 4px 24px oklch(0 0 0 / 0.6), inset 0 1px 0 oklch(0.25 0.01 240 / 0.4)",
          }}
        >
          {/* Commentator portraits — overlapping the top edge */}
          <div
            className="absolute left-1/2 flex -translate-x-1/2 items-center gap-4"
            style={{ top: -28 }}
          >
            {recap.commentators.map((c) => (
              <SmallPortrait
                key={c.key}
                commentatorKey={c.key}
                name={c.name}
                portraitUrl={recap.portrait_urls[`${c.key}_neutral`] ?? null}
              />
            ))}
          </div>

          <div className="px-8">
            {/* Section header */}
            <SegmentHeader
              rules={false}
              className="text-center"
              label="Final Analysis"
            />

            <NeonDivider className="mx-auto my-6 max-w-48" />

            {/* Stat comparison bars */}
            <StatComparisonBars stats={stats} match={match} />

            {/* MVP spotlight */}
            {mvps.length > 0 && announcer && (
              <>
                <NeonDivider className="mx-auto my-6 max-w-48" />
                <MvpSpotlight
                  mvps={mvps}
                  match={match}
                  commentatorKey={announcer.key}
                  commentatorName={announcer.name}
                  portraitUrl={
                    recap.portrait_urls[`${announcer.key}_neutral`] ?? null
                  }
                />
              </>
            )}

            {/* Injury ticker */}
            {injuries.length > 0 && (
              <>
                <NeonDivider className="mx-auto my-6 max-w-48" />
                <InjuryTicker injuries={injuries} match={match} />
              </>
            )}

            {/* Top performers */}
            {sppEarned.length > 0 && (
              <>
                <NeonDivider className="mx-auto my-6 max-w-48" />
                <TopPerformers
                  sppEarned={sppEarned}
                  match={match}
                  leagueId={leagueId}
                />
              </>
            )}

            {/* Watch Full Replay CTA */}
            <div className="mt-8 text-center">
              <Button
                variant="gold"
                className="h-12 px-8 text-xl"
                onClick={() => router.visit(replayHref)}
              >
                <Play className="mr-2 size-5" fill="currentColor" />
                Watch Full Replay
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Back link — below the panel */}
      <div className="mt-6 text-center">
        <Link
          href={backHref}
          className="text-sm transition-colors hover:text-[var(--foreground)]"
          style={{ color: "var(--muted-foreground)" }}
        >
          &larr; Back to {leagueName}
        </Link>
      </div>

      {/* Neon divider sign-off */}
      <NeonDivider glow className="mx-auto mt-6 w-full" />
    </div>
  )
}
